// src/pages/Utilisateurs/usersAdminUtils.js

export const DEFAULT_ROLE_OPTIONS = [
  { id: 1, label: "Administrateur" },
  { id: 2, label: "Demo" },
  { id: 3, label: "TournoiPro" },
  { id: 4, label: "TournoiUltim" },
];

export function getUserId(user) {
  return user?.id ?? user?.user_id ?? user?.userId ?? null;
}

export function getUserName(user) {
  const fullName = `${user?.prenom || ""} ${user?.nom || ""}`.trim();
  return fullName || "Utilisateur inconnu";
}

export function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
}

export function toUserDate(value) {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getUserCreatedDate(user) {
  return (
    user?.created_at ||
    user?.createdAt ||
    user?.createdOn ||
    user?.date_creation ||
    user?.updated_at ||
    null
  );
}

export function formatDateTimeFR(value) {
  const date = toUserDate(value);
  if (!date) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function normalizeRoleOptions(rolesData) {
  if (!Array.isArray(rolesData) || rolesData.length === 0) {
    return DEFAULT_ROLE_OPTIONS;
  }

  return rolesData
    .map((role) => ({
      id: Number(role.id),
      label: role.nom || role.name || role.label || `Rôle ${role.id}`,
    }))
    .filter((role) => Number.isInteger(role.id))
    .sort((a, b) => a.id - b.id);
}

export function buildRolesMap(roleOptions = []) {
  return roleOptions.reduce((acc, role) => {
    acc[role.id] = role.label;
    return acc;
  }, {});
}

export function getRoleLabel(roleId, rolesMap = {}) {
  const id = Number(roleId);
  return (
    rolesMap[id] ||
    DEFAULT_ROLE_OPTIONS.find((role) => role.id === id)?.label ||
    "Rôle inconnu"
  );
}

export function getRoleTone(roleId) {
  const id = Number(roleId);

  if (id === 1) {
    return "bg-red-100 text-red-700 dark:bg-red-950/70 dark:text-red-300";
  }

  if (id === 2) {
    return "bg-blue-100 text-blue-700 dark:bg-blue-950/70 dark:text-blue-300";
  }

  if (id === 3) {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-300";
  }

  if (id === 4) {
    return "bg-violet-100 text-violet-700 dark:bg-violet-950/70 dark:text-violet-300";
  }

  return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";
}

export function safeDisciplines(raw) {
  try {
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (typeof raw === "string" && raw.trim() === "") return [];

    let parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }

    if (Array.isArray(parsed)) return parsed.filter(Boolean);

    if (parsed) return [String(parsed)];

    return [];
  } catch {
    return raw ? [String(raw)] : ["Judo"];
  }
}

export function formatCompetitionDate(value) {
  const date = toUserDate(value);
  if (!date) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function openCompetitionPublicPage(competitionId) {
  if (!competitionId) return;

  window.open(
    `/competition/${competitionId}`,
    "_blank",
    "noopener,noreferrer"
  );
}

function csvCell(value) {
  const text = String(value ?? "").replace(/\r?\n|\r/g, " ");
  return `"${text.replace(/"/g, '""')}"`;
}

export function buildUsersCSV(users = [], rolesMap = {}, competitionsMap = {}) {
  const headers = [
    "id",
    "nom",
    "prenom",
    "email",
    "role",
    "role_id",
    "competitions_count",
  ];

  const rows = users.map((user) => {
    const id = getUserId(user);
    const competitions = competitionsMap[id] || [];

    return [
      id || "",
      user.nom || "",
      user.prenom || "",
      user.email || "",
      getRoleLabel(user.role_id, rolesMap),
      user.role_id || "",
      competitions.length,
    ];
  });

  return [
    headers.map(csvCell).join(";"),
    ...rows.map((row) => row.map(csvCell).join(";")),
  ].join("\n");
}

export function downloadCSV(filename, content) {
  const blob = new Blob(["\uFEFF" + content], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
}